
import React from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Users, Award, Link2, DollarSign } from "lucide-react";
import StatCard from "./StatCard";

interface ReferralRelationship {
  referred_id: string;
  paid_status: boolean;
}

interface UserWithReferrals {
  id: string;
  referral_code?: string;
  referral_relationships: ReferralRelationship[];
}

interface ReferralStatsCardProps {
  users?: UserWithReferrals[];
}

const ReferralStatsCard: React.FC<ReferralStatsCardProps> = ({ users }) => {
  // Calculate total number of referral relationships across all users
  const totalReferrals = React.useMemo(() => {
    if (!users?.length) return 0;
    
    return users.reduce((total, user) => {
      return total + (user.referral_relationships?.length || 0);
    }, 0);
  }, [users]);
  
  // Count paid referrals (relationships with paid_status=true)
  const paidReferrals = React.useMemo(() => {
    if (!users?.length) return 0;
    
    return users.reduce((total, user) => {
      const paidRelationships = (user.referral_relationships || [])
        .filter(rel => rel.paid_status === true);
      return total + paidRelationships.length;
    }, 0);
  }, [users]);
  
  // Free referrals are total minus paid
  const freeReferrals = totalReferrals - paidReferrals;
  
  // Count unique affiliate IDs (users who have referred others)
  const uniqueAffiliateIds = React.useMemo(() => {
    if (!users?.length) return 0;
    
    return users.filter(user => 
      user.referral_relationships && user.referral_relationships.length > 0
    ).length;
  }, [users]);
  
  // Calculate conversion rate (percentage of paid referrals)
  const conversionRate = totalReferrals > 0 
    ? Math.round((paidReferrals / totalReferrals) * 100) 
    : 0;
  
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium">Referral Statistics</h3>
      <div className="grid gap-4 md:grid-cols-3">
        <StatCard 
          title="Total Referrals"
          value={totalReferrals}
          subtitle="Users acquired through referrals"
          icon={Users}
        />
        
        <StatCard 
          title="Paid Referrals"
          value={paidReferrals}
          subtitle="Converted referrals with payments"
          icon={Award}
        />
        
        <StatCard 
          title="Free Referrals" 
          value={freeReferrals}
          subtitle="Non-paying referred users"
          icon={Users}
        />
      </div>
      
      <div className="grid gap-4 md:grid-cols-2">
        <StatCard 
          title="Unique Affiliates"
          value={uniqueAffiliateIds}
          subtitle="Active referring affiliates"
          icon={Link2}
        />
        
        <StatCard 
          title="Conversion Rate"
          value={`${conversionRate}%`}
          subtitle="Referrals that converted to paid"
          icon={DollarSign}
        />
      </div>
    </div>
  );
};

export default ReferralStatsCard;
